/** @format */

import React, { useRef, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { Box, Container } from "@mui/material";
import { trackVideoInteraction } from "../utils/analytics";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    minHeight: "100vh",
    paddingTop: theme.spacing(25),
    paddingBottom: theme.spacing(10),
  },
  iframeContainer: {
    position: "relative",
    paddingBottom: "56.25%" /* 16:9 aspect ratio */,
    height: 0,
    overflow: "hidden",
    marginTop: theme.spacing(4),
  },
  iframe: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
}));

const Intro: React.FC = () => {
  const classes = useStyles();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoName = "EON Introduction Video";

  useEffect(() => {
    // Function to handle messages from YouTube iframe API
    const handleMessage = (event: MessageEvent) => {
      // Make sure the message is from YouTube
      if (event.origin !== "https://www.youtube.com") return;

      try {
        const data = JSON.parse(event.data);

        // Check if this is a YouTube player event
        if (data.event === "onStateChange") {
          switch (data.info) {
            case 1: // Video started playing
              trackVideoInteraction(videoName, "play");
              break;
            case 0: // Video ended
              trackVideoInteraction(videoName, "complete");
              break;
            // We can add more states if needed
          }
        } else if (data.info && typeof data.info.currentTime !== "undefined") {
          // Track progress at 25%, 50%, 75%
          const duration = data.info.duration;
          const currentTime = data.info.currentTime;
          const percentage = Math.floor((currentTime / duration) * 100);

          if (percentage === 25 || percentage === 50 || percentage === 75) {
            trackVideoInteraction(videoName, "progress", percentage);
          }
        }
      } catch (err) {
        console.error("Error parsing YouTube event:", err);
      }
    };

    // Add event listener for postMessage events
    window.addEventListener("message", handleMessage);

    // Initialize YouTube iframe API
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const src = iframe.src;

      // Add enablejsapi=1 parameter to the video URL if not already present
      if (!src.includes("enablejsapi=1")) {
        iframe.src = `${src}${
          src.includes("?") ? "&" : "?"
        }enablejsapi=1&origin=${window.location.origin}`;
      }
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [videoName]);

  return (
    <Box className={classes.root}>
      <Container maxWidth="md">
        <h1 className="text-3xl font-bold mb-6">Introduction Video</h1>
        <div className={classes.iframeContainer}>
          <iframe
            ref={iframeRef}
            src="https://www.youtube.com/embed/Uv194E6TkF8"
            title="Intro Video"
            className={classes.iframe}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Container>
    </Box>
  );
};

export default Intro;
